import React, { useRef } from "react";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { darkBg, gridColor, labelColor, makeGradient, tooltipStyle } from "../api/echartsTheme";
import GraphDownloadButton from "../../Global/other/GraphDownloadButton";
import { formatSAST } from "../../Global/other/formatSAST";
import {
  useGetADPGraphQuery,
  useGetADPPeakSummaryQuery,
} from "../OperatorDashboard/operatorDashboardAPISlice";

const color = "#DC6CFE";

export default function ADPGraph({ startDate, endDate }) {
  const chartRef = useRef(null);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const request = {
    start_date: startDate ? startDate.format("YYYY-MM-DD") : undefined,
    end_date: endDate ? endDate.format("YYYY-MM-DD") : undefined,
    user_timezone: userTimezone,
  };

  const { data: graphData, isLoading: graphLoading, isError: graphError } =
    useGetADPGraphQuery(request);

  const { data: peakSummary, isLoading: summaryLoading } =
    useGetADPPeakSummaryQuery();

  const handleDownloadCsv = () => {
    if (!graphData?.timestamps) return;
    let csv = "Timestamp (SAST),ADP (kW/geyser)\r\n";
    graphData.timestamps.forEach((ts, i) => {
      const val =
        graphData.adp_values[i] != null
          ? Number(graphData.adp_values[i]).toFixed(4)
          : "";
      csv += `${formatSAST(ts)},${val}\r\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ADP_${dayjs().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPng = () => {
    const url = chartRef.current
      ?.getEchartsInstance()
      ?.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: darkBg });
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `ADP_${dayjs().format("YYYY-MM-DD")}.png`;
    a.click();
  };

  const seriesData = (graphData?.timestamps || []).map((ts, i) => [
    new Date(ts).getTime(),
    graphData.adp_values[i] ?? null,
  ]);

  const option = {
    backgroundColor: darkBg,
    animation: false,
    grid: { top: 36, right: 24, bottom: 76, left: 64 },
    dataZoom: [
      { type: "inside", xAxisIndex: 0, filterMode: "filter" },
      {
        type: "slider",
        xAxisIndex: 0,
        height: 18,
        bottom: 32,
        backgroundColor: "rgba(255,255,255,0.03)",
        borderColor: "rgba(255,255,255,0.07)",
        fillerColor: color + "33",
        handleStyle: { color, borderColor: color },
        moveHandleStyle: { color },
        emphasis: { handleStyle: { color } },
        textStyle: { color: "transparent" },
        brushSelect: false,
        showDetail: false,
      },
    ],
    tooltip: {
      trigger: "axis",
      ...tooltipStyle,
      formatter: (params) =>
        params
          .filter((p) => p.value?.[1] != null)
          .map(
            (p) =>
              `${p.seriesName}: <b>${Number(p.value[1]).toFixed(4)} kW/geyser</b>`
          )
          .join("<br/>"),
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { lineStyle: { color: gridColor } },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        formatter: (v) => {
          const d = new Date(v);
          return `${d.getHours().toString().padStart(2, "0")}:${d
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;
        },
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      name: "kW/geyser",
      nameTextStyle: { color: labelColor, fontSize: 11 },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        formatter: (v) => v.toFixed(3),
      },
      splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
    },
    series: [
      {
        name: "ADP",
        type: "line",
        smooth: true,
        symbol: "none",
        connectNulls: false,
        lineStyle: { color, width: 2 },
        areaStyle: { color: makeGradient(color), opacity: 0.4 },
        itemStyle: { color },
        data: seriesData,
      },
    ],
  };

  // ── Render ──

  const graphContent = (() => {
    if (graphLoading) {
      return (
        <Grid item sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <CircularProgress size={20} sx={{ color }} />
          <Typography sx={{ color: labelColor, fontSize: "0.82rem" }}>
            Loading ADP data…
          </Typography>
        </Grid>
      );
    }
    if (graphError || !graphData) {
      return (
        <Grid item sx={{ px: 2, pb: 2 }}>
          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.25)", fontStyle: "italic" }}>
            ADP data not yet available — backend computation pending.
          </Typography>
        </Grid>
      );
    }
    return (
      <Grid item sx={{ width: "100%", p: 2 }}>
        <ReactECharts ref={chartRef} option={option} style={{ height: 340 }} notMerge />
      </Grid>
    );
  })();

  // Peak summary values
  const current = peakSummary?.current_month_adp;
  const previous = peakSummary?.previous_month_adp;
  const currentLabel = peakSummary?.current_month_label ?? "This month";
  const previousLabel = peakSummary?.previous_month_label ?? "Previous month";
  const momChange = current != null && previous != null ? current - previous : null;
  const momPct =
    momChange != null && previous !== 0
      ? (momChange / previous) * 100
      : null;
  const isUp = momChange != null && momChange > 0;

  return (
    <Card sx={{ borderRadius: "12px", backgroundColor: darkBg }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: 2,
          px: 2,
          pb: "4px",
        }}
      >
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}>
            Average Demand per Geyser (ADP)
          </Typography>
          <Typography sx={{ color: labelColor, fontSize: "0.75rem", mt: "2px" }}>
            Total sampled load ÷ sampled device count · kW/geyser · weekdays
          </Typography>
        </Box>
        {!graphError && !graphLoading && graphData && (
          <GraphDownloadButton
            onDownloadCsv={handleDownloadCsv}
            onDownloadPng={handleDownloadPng}
          />
        )}
      </Box>

      {/* Graph */}
      {graphContent}

      {/* Peak-period summary */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "12px",
          px: 2,
          pb: 2,
          pt: graphError || !graphData ? "8px" : 0,
        }}
      >
        {[
          {
            label: currentLabel,
            value: current,
            sub: "weekdays only",
            valueColor: "#fff",
          },
          {
            label: previousLabel,
            value: previous,
            sub: "final",
            valueColor: labelColor,
          },
          {
            label: "Month-over-month",
            value: momChange,
            sub:
              momPct != null
                ? `${momPct > 0 ? "+" : ""}${momPct.toFixed(1)}%`
                : null,
            valueColor: isUp ? "#e05050" : "#26BF00",
            prefix: momChange != null && momChange > 0 ? "+" : "",
            icon: summaryLoading
              ? null
              : momChange != null
              ? isUp
                ? <ArrowUpwardIcon sx={{ fontSize: "0.9rem", color: "#e05050" }} />
                : <ArrowDownwardIcon sx={{ fontSize: "0.9rem", color: "#26BF00" }} />
              : null,
          },
        ].map(({ label, value, sub, valueColor, prefix, icon }) => (
          <Box
            key={label}
            sx={{
              backgroundColor: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "8px",
              p: "14px 16px",
            }}
          >
            <Typography
              sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", mb: "6px" }}
            >
              {label}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {summaryLoading ? (
                <CircularProgress size={14} sx={{ color }} />
              ) : (
                <>
                  <Typography
                    sx={{ fontSize: "1.25rem", fontWeight: 700, color: valueColor, fontVariantNumeric: "tabular-nums" }}
                  >
                    {value != null
                      ? `${prefix ?? ""}${Number(value).toFixed(3)}`
                      : "—"}
                  </Typography>
                  {value != null && (
                    <Typography sx={{ fontSize: "0.72rem", color: labelColor, fontWeight: 500 }}>
                      kW/geyser
                    </Typography>
                  )}
                  {icon}
                </>
              )}
            </Box>
            {sub && value != null && (
              <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.3)", mt: "2px" }}>
                {sub}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Card>
  );
}
